import { createQueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'

import { FilterType, QuickSearchApiClient, QuickSearchFilters } from '@/scopes/search/quick-search/domain/types'
import { createRoomsParticipantDistributionMapper } from '@/scopes/search/search/infrastructure/api/filtersSelectionMapper/mappers/roomsParticipantDistributionMapper'
import defaultFetch from 'isomorphic-unfetch'
import { createQuickSearchFiltersParser } from './quickSearchFiltersParser'
import { createQuickSearchResponseMapper } from './quickSearchResponseMapper'

interface QuickSearchContext {
  contextItemId?: string
  publicationCode?: string
  requiredFilters?: FilterType[]
}

type Criteria = QuickSearchContext & {
  filterSelection?: QuickSearchFilters
}

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  quickSearchFilterResponseMapper: ReturnType<typeof createQuickSearchResponseMapper>
  roomsParticipantDistributionMapper: ReturnType<typeof createRoomsParticipantDistributionMapper>
  contextItemId: string
}

export function createQuickSearchApiClient(dependencies: Partial<Dependencies>): QuickSearchApiClient {
  const {
    fetch = defaultFetch,
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    quickSearchFilterResponseMapper = createQuickSearchResponseMapper(),
    roomsParticipantDistributionMapper = createRoomsParticipantDistributionMapper(),
  } = dependencies

  const queryStringSerializer = createQueryStringSerializer<QuickSearchFilters & QuickSearchContext>({
    parser: createQuickSearchFiltersParser({ roomsParticipantDistributionMapper }),
  })

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }

  async function getQuickSearch(criteria: Criteria) {
    const url = buildAPIUrl(criteria)
    const response = await fetch(url, fetchOptions)

    const data = await response.json()

    return quickSearchFilterResponseMapper.map(data)
  }

  function buildAPIUrl(criteria: Criteria) {
    const { contextItemId, publicationCode, filterSelection, requiredFilters } = criteria

    const queryString = queryStringSerializer.serialize({
      contextItemId,
      publicationCode,
      ...filterSelection,
    })

    const requiredFiltersParam = requiredFilters?.join('%2B') ?? 'Participants%2BAllocation%2BParticipantsDistribution%2BDepartureAirport%2BDestination%2BDepartureDate%2BTransportType%2BDuration'

    return `${env.NEXT_PUBLIC_API_BASE_URL}/QuickSearch/GetQuickSearchApi?requiredFilters=${requiredFiltersParam}&${queryString}`
  }

  return {
    getQuickSearch,
  }
}
